<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { Marked } from 'marked'
import DOMPurify from 'dompurify'
import { MapPin, CheckCircle2, ChevronDown, ChevronUp, Bug } from 'lucide-vue-next'
import { formatTimeFromNow } from '@/utils/datetime'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { MediaSliderDialog } from '@/components/media'

// Agent replies are markdown; humans type plain text (rendered via segments()
// so @mentions highlight). Isolated instance, same pattern as ReportView.
const chatMarked = new Marked({ breaks: true, gfm: true })
chatMarked.use({
  renderer: {
    link({ href, text }) {
      return `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`
    },
  },
})

// Rendered once per distinct body: this runs from the template, so without a
// memo every agent message re-parses and re-sanitizes on each feed re-render.
const markdownCache = new Map()
const MARKDOWN_CACHE_MAX = 500

function renderAgentMarkdown(text) {
  const key = text || ''
  const cached = markdownCache.get(key)
  if (cached !== undefined) return cached
  const html = renderMarkdownUncached(key)
  if (markdownCache.size >= MARKDOWN_CACHE_MAX) markdownCache.clear()
  markdownCache.set(key, html)
  return html
}

function renderMarkdownUncached(text) {
  return DOMPurify.sanitize(chatMarked.parse(text || ''), {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'b', 'i', 'code', 'pre',
      'ul', 'ol', 'li', 'a', 'blockquote', 'del', 'hr',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  })
}
import ChatAvatar from './ChatAvatar.vue'
import ChatAttachment from './ChatAttachment.vue'
import ChatComposer from './ChatComposer.vue'
import MessageReactions from './MessageReactions.vue'

const props = defineProps({
  message: { type: Object, required: true },
  variant: { type: String, default: 'feed' }, // 'feed' | 'popup'
  // Off in 1:1 messaging, where threads under each message are just noise.
  showReplies: { type: Boolean, default: true },
  members: { type: Array, default: () => [] },
})

const emit = defineEmits(['reply', 'locate'])

const store = useStore()
const currentId = computed(() => store.getters['user/getMember']?.member_id)
const me = computed(() => store.getters['user/getMember'])
const isOwn = computed(() => props.message.author?.member_id === currentId.value)
const isPopup = computed(() => props.variant === 'popup')

const expanded = ref(false)

const timeAgo = (ts) => formatTimeFromNow(new Date(ts).toISOString())

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

function segments(text, mentions) {
  // Longest first so @vlad_simankov2 is not eaten by @vlad_simankov;
  // display_name kept as fallback for messages sent before nicknames existed.
  const names = [
    ...new Set((mentions || []).flatMap((m) => [m.nickname, m.display_name]).filter(Boolean)),
  ].sort((a, b) => b.length - a.length)
  if (!names.length || !text) return [{ mention: false, v: text }]
  const re = new RegExp(
    `(${names.map((n) => '@' + escapeRe(n) + '(?![a-zA-Z0-9._])').join('|')})`,
    'g'
  )
  return text
    .split(re)
    .filter(Boolean)
    .map((v) => ({ mention: v.startsWith('@') && names.includes(v.slice(1)), v }))
}

const popupEntries = computed(() => [props.message, ...(props.message.replies || [])])
const isOwnEntry = (entry) => entry.author?.member_id === currentId.value

const onToggleReaction = (msg, emoji) =>
  store.dispatch('chat/toggleReaction', { uuid: msg.conversationUuid, messageId: msg.id, emoji })

const onRetry = (msg) =>
  store.dispatch('chat/retryMessage', { uuid: msg.conversationUuid, messageUuid: msg.uuid })
const onDiscard = (msg) =>
  store.dispatch('chat/discardMessage', { uuid: msg.conversationUuid, messageUuid: msg.uuid })

const replyAuthors = computed(() => {
  const seen = new Set()
  const authors = []
  for (const reply of props.message.replies || []) {
    const id = reply.author?.member_id
    if (id && !seen.has(id)) {
      seen.add(id)
      authors.push(reply.author)
    }
  }
  return authors.slice(0, 3)
})

const lightboxOpen = ref(false)
const lightboxImages = ref([])
const lightboxIndex = ref(0)

// Prev/next walks only the images of the entry that was clicked
function openImage(attachments, attachment) {
  const images = (attachments || []).filter((a) => a.type === 'image')
  lightboxImages.value = images
  lightboxIndex.value = Math.max(images.indexOf(attachment), 0)
  lightboxOpen.value = true
}

// --- AI debug stats (super admins only; server re-checks the flag) ---

const showDebug = computed(
  () => !!props.message.author?.is_agent && !!me.value?.is_super_user
)
const debugMeta = ref(null)
const debugState = ref('idle') // idle | loading | loaded | error

async function onDebugOpen(open) {
  if (!open || debugState.value === 'loaded' || debugState.value === 'loading') return
  debugState.value = 'loading'
  try {
    debugMeta.value = await store.dispatch('chat/fetchMessageDebug', {
      uuid: props.message.conversationUuid,
      messageId: props.message.id,
    })
    debugState.value = 'loaded'
  } catch (e) {
    debugState.value = 'error'
  }
}

const debugRows = computed(() => {
  const meta = debugMeta.value
  if (!meta) return []
  const rows = [
    ['Model', meta.model],
    ['Duration', meta.duration_ms != null ? `${(meta.duration_ms / 1000).toFixed(1)}s` : null],
    ['Requests', meta.requests],
    ['Tokens in', meta.input_tokens],
    ['Tokens out', meta.output_tokens],
    ['Cache read', meta.cache_read_tokens],
    ['Cache write', meta.cache_write_tokens],
  ]
  return rows.filter(([, value]) => value != null)
})
</script>

<template>
  <!-- Popup variant: flat list of the root message and replies as chat bubbles -->
  <div v-if="isPopup" class="space-y-3">
    <div
      v-for="entry in popupEntries"
      :key="entry.uuid"
      class="flex gap-2"
      :class="[isOwnEntry(entry) ? 'flex-row-reverse' : '', entry.pending ? 'opacity-60' : '']"
    >
      <ChatAvatar :author="entry.author" size="sm" class="mt-0.5" />
      <div class="flex min-w-0 flex-1 flex-col gap-1" :class="isOwnEntry(entry) ? 'items-end' : 'items-start'">
        <div class="flex items-baseline gap-1.5 text-xs" :class="isOwnEntry(entry) ? 'flex-row-reverse' : ''">
          <span class="font-semibold">{{ entry.author.display_name }}</span>
          <span class="text-muted-foreground">{{ timeAgo(entry.createdAt) }}</span>
        </div>
        <div
          class="w-fit max-w-[90%] rounded-lg px-3 py-2 text-sm"
          :class="isOwnEntry(entry) ? 'bg-primary text-primary-foreground' : 'bg-muted'"
        >
          <div
            v-if="entry.author?.is_agent"
            class="chat-md break-words"
            v-html="renderAgentMarkdown(entry.text)"
          ></div>
          <p v-else class="whitespace-pre-wrap break-words">
            <template v-for="(seg, i) in segments(entry.text, entry.mentions)" :key="i">
              <span
                v-if="seg.mention"
                class="rounded px-0.5 font-medium"
                :class="isOwnEntry(entry) ? 'bg-white/20 text-white' : 'bg-primary/15 text-primary'"
              >{{ seg.v }}</span>
              <template v-else>{{ seg.v }}</template>
            </template>
          </p>
          <div v-if="entry.attachments?.length" class="mt-2 flex flex-wrap gap-1.5">
            <ChatAttachment
              v-for="(a, i) in entry.attachments"
              :key="i"
              :attachment="a"
              @open="openImage(entry.attachments, a)"
            />
          </div>
        </div>
        <div v-if="entry.failed" class="flex items-center gap-2 text-xs text-destructive">
          Failed to send
          <button class="font-medium underline" @click="onRetry(entry)">Retry</button>
          <button class="text-muted-foreground underline" @click="onDiscard(entry)">Discard</button>
        </div>
        <MessageReactions
          :reactions="entry.reactions"
          :current-member-id="currentId"
          :reverse="isOwnEntry(entry)"
          @toggle="(e) => onToggleReaction(entry, e)"
        />
      </div>
    </div>
  </div>

  <!-- Feed variant: chat bubble with collapsible thread -->
  <div v-else class="flex gap-2" :class="isOwn ? 'flex-row-reverse' : ''">
    <ChatAvatar :author="message.author" size="base" class="mt-0.5" />
    <div class="min-w-0 flex-1">
      <div class="flex flex-col gap-1" :class="isOwn ? 'items-end' : 'items-start'">
        <div class="flex items-baseline gap-1.5 text-xs" :class="isOwn ? 'flex-row-reverse' : ''">
          <span class="font-semibold">{{ message.author.display_name }}</span>
          <span class="text-muted-foreground">{{ timeAgo(message.createdAt) }}</span>
          <CheckCircle2 v-if="message.resolved" class="size-3.5 self-center text-emerald-600" />
        </div>

        <div class="flex w-fit max-w-[90%] flex-col gap-1" :class="isOwn ? 'items-end' : 'items-start'">
        <div
          class="w-fit rounded-lg px-3 py-2 text-sm"
          :class="[isOwn ? 'bg-primary text-primary-foreground' : 'bg-muted', message.pending ? 'opacity-60' : '']"
        >
          <div
            v-if="message.author?.is_agent"
            class="chat-md break-words"
            v-html="renderAgentMarkdown(message.text)"
          ></div>
          <p v-else class="whitespace-pre-wrap break-words">
            <template v-for="(seg, i) in segments(message.text, message.mentions)" :key="i">
              <span
                v-if="seg.mention"
                class="rounded px-0.5 font-medium"
                :class="isOwn ? 'bg-white/20 text-white' : 'bg-primary/15 text-primary'"
              >{{ seg.v }}</span>
              <template v-else>{{ seg.v }}</template>
            </template>
          </p>
          <div v-if="message.attachments?.length" class="mt-2 flex flex-wrap gap-1.5">
            <ChatAttachment
              v-for="(a, i) in message.attachments"
              :key="i"
              :attachment="a"
              @open="openImage(message.attachments, a)"
            />
          </div>
        </div>

        <div v-if="message.failed" class="flex items-center gap-2 text-xs text-destructive">
          Failed to send
          <button class="font-medium underline" @click="onRetry(message)">Retry</button>
          <button class="text-muted-foreground underline" @click="onDiscard(message)">Discard</button>
        </div>

        <MessageReactions
          :reactions="message.reactions"
          :current-member-id="currentId"
          :reverse="isOwn"
          @toggle="(e) => onToggleReaction(message, e)"
        />

        <div
          v-if="showReplies || message.location || showDebug"
          class="flex w-full items-center gap-3 text-xs text-muted-foreground"
          :class="isOwn ? 'flex-row-reverse' : ''"
        >
          <button
            v-if="showReplies"
            class="inline-flex items-center gap-1.5 transition-colors hover:text-foreground"
            @click="expanded = !expanded"
          >
            <template v-if="message.replies?.length">
              <span class="flex items-center">
                <ChatAvatar
                  v-for="(author, i) in replyAuthors"
                  :key="author.member_id"
                  :author="author"
                  size="xs"
                  class="ring-2 ring-background"
                  :class="i > 0 ? '-ml-1.5' : ''"
                />
              </span>
              <span class="font-medium text-primary">
                {{ message.replies.length }} {{ message.replies.length === 1 ? 'reply' : 'replies' }}
              </span>
            </template>
            <template v-else>
              <ChevronUp v-if="expanded" class="size-3.5" />
              <ChevronDown v-else class="size-3.5" />
              Reply
            </template>
          </button>
          <button
            v-if="message.location"
            class="inline-flex items-center gap-1 transition-colors hover:text-primary"
            :class="isOwn ? 'mr-auto' : 'ml-auto'"
            title="Show on map"
            @click="emit('locate')"
          >
            <MapPin class="size-3.5" />
            On map
          </button>
          <Popover v-if="showDebug" @update:open="onDebugOpen">
            <PopoverTrigger as-child>
              <button
                class="inline-flex items-center transition-colors hover:text-foreground"
                title="AI debug stats"
              >
                <Bug class="size-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent class="w-80 p-3 text-xs" align="start">
              <div v-if="debugState === 'loading'" class="text-muted-foreground">Loading…</div>
              <div v-else-if="debugState === 'error'" class="text-destructive">Failed to load debug stats.</div>
              <div v-else-if="debugState === 'loaded' && !debugMeta" class="text-muted-foreground">
                No debug data for this message.
              </div>
              <div v-else-if="debugState === 'loaded'" class="space-y-2">
                <div class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                  <template v-for="[label, value] in debugRows" :key="label">
                    <span class="text-muted-foreground">{{ label }}</span>
                    <span class="truncate font-medium">{{ value }}</span>
                  </template>
                </div>
                <div v-if="debugMeta.tool_calls?.length">
                  <div class="mb-1 font-semibold">Tools ({{ debugMeta.tool_calls.length }})</div>
                  <div
                    v-for="(call, i) in debugMeta.tool_calls"
                    :key="i"
                    class="mb-1 rounded bg-muted p-1.5"
                  >
                    <div class="font-mono font-medium">{{ call.name }}</div>
                    <div v-if="call.args && call.args !== '{}'" class="truncate font-mono text-muted-foreground">
                      {{ call.args }}
                    </div>
                  </div>
                </div>
                <div v-else class="text-muted-foreground">No tools called.</div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        </div>

        <div
          v-if="expanded && showReplies"
          class="w-full space-y-2.5 pt-1"
          :class="isOwn ? 'border-r-2 border-border pr-3' : 'border-l-2 border-border pl-3'"
        >
          <div v-for="reply in message.replies" :key="reply.uuid" class="flex gap-2" :class="[isOwn ? 'flex-row-reverse' : '', reply.pending ? 'opacity-60' : '']">
            <ChatAvatar :author="reply.author" size="sm" class="mt-0.5" />
            <div class="min-w-0 flex-1 flex flex-col gap-0.5" :class="isOwn ? 'items-end' : 'items-start'">
              <div class="flex items-baseline gap-1.5 text-xs" :class="isOwn ? 'flex-row-reverse' : ''">
                <span class="font-semibold">{{ reply.author.display_name }}</span>
                <span class="text-muted-foreground">{{ timeAgo(reply.createdAt) }}</span>
              </div>
              <div
                v-if="reply.author?.is_agent"
                class="chat-md break-words text-sm"
                v-html="renderAgentMarkdown(reply.text)"
              ></div>
              <p v-else class="whitespace-pre-wrap break-words text-sm">
                <template v-for="(seg, i) in segments(reply.text, reply.mentions)" :key="i">
                  <span v-if="seg.mention" class="rounded bg-primary/15 px-0.5 font-medium text-primary">{{ seg.v }}</span>
                  <template v-else>{{ seg.v }}</template>
                </template>
              </p>
              <div v-if="reply.attachments?.length" class="mt-1 flex flex-wrap gap-1.5">
                <ChatAttachment
                  v-for="(a, i) in reply.attachments"
                  :key="i"
                  :attachment="a"
                  @open="openImage(reply.attachments, a)"
                />
              </div>
              <div v-if="reply.failed" class="flex items-center gap-2 text-xs text-destructive">
                Failed to send
                <button class="font-medium underline" @click="onRetry(reply)">Retry</button>
                <button class="text-muted-foreground underline" @click="onDiscard(reply)">Discard</button>
              </div>
              <MessageReactions
                :reactions="reply.reactions"
                :current-member-id="currentId"
                :reverse="isOwn"
                @toggle="(e) => onToggleReaction(reply, e)"
              />
            </div>
          </div>

          <ChatComposer
            :members="members"
            :data-attachments="false"
            placeholder="Reply"
            @send="(p) => emit('reply', p)"
          />
        </div>
      </div>
    </div>
  </div>

  <MediaSliderDialog
    v-if="lightboxOpen"
    :resources="lightboxImages"
    :initial-index="lightboxIndex"
    :open="lightboxOpen"
    :show-delete="false"
    @update:open="lightboxOpen = $event"
  />
</template>

<style scoped>
/* Markdown inside agent bubbles (v-html, hence :deep) — compact chat scale. */
.chat-md :deep(p + p),
.chat-md :deep(ul + p),
.chat-md :deep(ol + p),
.chat-md :deep(p + ul),
.chat-md :deep(p + ol) {
  margin-top: 0.5em;
}
.chat-md :deep(ul) {
  list-style: disc;
  padding-left: 1.25em;
}
.chat-md :deep(ol) {
  list-style: decimal;
  padding-left: 1.25em;
}
.chat-md :deep(code) {
  font-family: ui-monospace, monospace;
  font-size: 0.85em;
  background: hsl(var(--foreground) / 0.08);
  border-radius: 3px;
  padding: 0.1em 0.3em;
}
.chat-md :deep(pre) {
  background: hsl(var(--foreground) / 0.08);
  border-radius: 6px;
  padding: 0.5em;
  overflow-x: auto;
  margin: 0.5em 0;
}
.chat-md :deep(pre code) {
  background: none;
  padding: 0;
}
.chat-md :deep(a) {
  color: hsl(var(--primary));
  text-decoration: underline;
}
.chat-md :deep(blockquote) {
  border-left: 2px solid hsl(var(--border));
  padding-left: 0.6em;
  color: hsl(var(--muted-foreground));
  margin: 0.4em 0;
}
</style>
